import { FC, ReactElement, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useSearchParams } from "next/navigation";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateContext } from "../../../Context/RateContext";
import { useRateFieldError } from "../../../hooks/useRateFieldError";
import { useRateFieldName } from "../../../hooks/useRateFieldName";
import { useRatesExpansion } from "../../../../RatesExpansion/RatesExpansionContext";
import { useTriggerRateOptionHighlight } from "../../../../RateOptionHighlight/RateOptionHighlightContext";
import { RateOptionToggleRow } from "../../../shared/RateOptions/RateOptionToggleRow";

import { detectGroupMembership } from "@/common/helpers/detectGroupMembership";
import { toFiniteNumber } from "@/common/helpers/toFiniteNumber";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const GroupMembershipRow: FC = () => {
  const { t } = useTranslate();
  const { rateId } = useRateContext();
  const { control, setValue, getValues, clearErrors } = useFormContext<StepTicketsFormData>();
  const { expandedRateId, setExpandedRateId } = useRatesExpansion();
  const highlight = useTriggerRateOptionHighlight();
  const searchParams = useSearchParams();
  const migrationRequested = searchParams.has("groupMembership");
  const migrationRate = useRef(
    migrationRequested
      ? getValues("rates")?.find((rate) => detectGroupMembership(rate.name, rate.description))
      : undefined
  ).current;
  const audience = migrationRate
    ? detectGroupMembership(migrationRate.name, migrationRate.description)?.audience
    : null;
  const [showMigrationTooltip, setShowMigrationTooltip] = useState(migrationRate?.id === rateId);
  const enabledField = useRateFieldName("isGroupMembership");
  const countField = useRateFieldName("groupMembershipMaxMembers");
  const maximumField = useRateFieldName("rateMaximumNumberToBuy");
  const enabled = useWatch({ control, name: enabledField });
  const error = useRateFieldError("groupMembershipMaxMembers");

  // Migration links open the matching tier's options before showing its helper.
  useEffect(() => {
    if (migrationRequested && migrationRate?.id === rateId) {
      setExpandedRateId(rateId);
      highlight(rateId, "groupTicket");
    }
  }, [migrationRequested, migrationRate?.id, rateId, setExpandedRateId, highlight]);

  const renderSwitch = (control: ReactElement) => (
    <DashboardTooltip
      open={showMigrationTooltip && !enabled && expandedRateId === rateId}
      placement="top-start"
      data-test={`group-membership-migration-${audience}`}
      PopperProps={{ modifiers: [{ name: "offset", options: { offset: [-8, 0] } }] }}
      slotProps={{
        tooltip: {
          sx: (theme) => ({
            width: theme.spacing(40),
            maxWidth: `calc(100vw - ${theme.spacing(4)})`,
            borderRadius: theme.radius(1),
            textWrap: "wrap",
            boxShadow: theme.boxShadow.base.mid,
            "&.MuiTooltip-tooltipPlacementTop": { mb: 1.25 },
            "&.MuiTooltip-tooltipPlacementBottom": { mt: 1.25 },
          }),
        },
        arrow: {
          sx: (theme) => ({
            width: theme.spacing(1.5),
            height: theme.spacing(0.75),
            "&::before": { transform: "none", clipPath: "polygon(0 0, 100% 0, 50% 100%)" },
            '[data-popper-placement*="top"] &&': { mb: -0.75 },
            '[data-popper-placement*="bottom"] &&': {
              mt: -0.75,
              "&::before": { clipPath: "polygon(0 100%, 100% 100%, 50% 0)" },
            },
          }),
        },
      }}
      title={
        audience ? (
          <Stack>
            <DashboardTypography variant="caption" sx={{ color: "inherit" }} fontWeight="bold">
              {t("dashboard", `ticketing.groupMembership.migration.${audience}.title`)}
            </DashboardTypography>
            <DashboardTypography variant="caption" sx={{ color: "inherit" }}>
              {t("dashboard", `ticketing.groupMembership.migration.${audience}.description`)}
            </DashboardTypography>
          </Stack>
        ) : (
          ""
        )
      }
    >
      {control}
    </DashboardTooltip>
  );

  return (
    <RateOptionToggleRow
      optionKey="groupTicket"
      renderSwitch={renderSwitch}
      belowContentGap={0.5}
      ariaLabel={t("dashboard", "ticketing.groupMembership")}
      checked={Boolean(enabled)}
      onToggle={(checked) => {
        setValue(enabledField, checked, { shouldDirty: true });
        setShowMigrationTooltip(false);
        if (checked && getValues(maximumField) <= 1) {
          setValue(maximumField, 10, { shouldDirty: true });
        }
        if (checked && !getValues(countField)) {
          setValue(countField, 2, { shouldDirty: true });
        }
        if (!checked) {
          clearErrors(countField);
        }
      }}
      label={
        <>
          {t("dashboard", "ticketing.groupMembership")}{" "}
          <DashboardTypography component="span" variant="caption" vibe="text-neutral-supershy">
            {t("dashboard", "ticketing.groupMembership.examples")}
          </DashboardTypography>
        </>
      }
      inlineConfig={
        enabled ? (
          <Stack direction="row" gap={0.5} alignItems="center">
            <DashboardTypography variant="caption" vibe="text-neutral-quiet">
              {t("dashboard", "ticketing.groupMembership.upTo")}
            </DashboardTypography>
            <Box sx={{ width: 40 }}>
              <DashboardTextField
                name={countField}
                control={control}
                type="number"
                size="small"
                error={Boolean(error)}
                rules={[
                  {
                    validate: (value: string | number | null | undefined) => {
                      if (!getValues(enabledField)) {
                        return true;
                      }
                      const count = toFiniteNumber(value);
                      return count !== null && Number.isInteger(count) && count >= 2 && count <= 500
                        ? true
                        : t("dashboard", "ticketing.groupMembership.maximumRequired");
                    },
                  },
                ]}
              />
            </Box>
            <DashboardTypography variant="caption" vibe="text-neutral-quiet">
              {t("dashboard", "ticketing.groupMembership.members")}
            </DashboardTypography>
          </Stack>
        ) : undefined
      }
      belowContent={
        enabled && error?.message ? (
          <DashboardTypography component="p" variant="caption" vibe="text-danger-moderate">
            {error.message}
          </DashboardTypography>
        ) : undefined
      }
    />
  );
};
